import { useEffect, useState } from "react";
import { api, ArtifactExpiredError } from "../api";
import { ExpiredPlaceholder } from "./ExpiredPlaceholder";

interface Props {
  jobId: string;
}

interface TimelineEvent {
  ts?: number;
  kind: string;
  text: string;
}

type State =
  | { kind: "loading" }
  | { kind: "expired"; error: ArtifactExpiredError }
  | { kind: "error"; message: string }
  | { kind: "ready"; events: TimelineEvent[] };

export function TimelineTab({ jobId }: Props) {
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    api
      .artifactTimeline(jobId)
      .then((view) => {
        if (cancelled) return;
        setState({ kind: "ready", events: extractEvents(view.body) });
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ArtifactExpiredError) {
          setState({ kind: "expired", error: e });
        } else {
          setState({
            kind: "error",
            message: e instanceof Error ? e.message : String(e),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  if (state.kind === "loading") return <p className="muted">Loading timeline…</p>;
  if (state.kind === "expired") return <ExpiredPlaceholder error={state.error} />;
  if (state.kind === "error") return <div className="error">{state.message}</div>;
  if (state.events.length === 0)
    return <p className="muted">Timeline was empty.</p>;

  return (
    <ul className="timeline-list">
      {state.events.map((ev, i) => (
        <li key={i} className={`timeline-row timeline-${ev.kind}`}>
          {ev.ts !== undefined && (
            <code className="muted timeline-ts">{formatTs(ev.ts)}</code>
          )}
          <span className={`timeline-kind ${ev.kind}`}>{ev.kind}</span>
          <span className="timeline-text">{ev.text}</span>
        </li>
      ))}
    </ul>
  );
}

/// Loose extraction. Worker writes either:
///   { events: [{ ts, kind, text }] }   — preferred
/// or
///   { moments: [{ ts, summary, ... }] }
/// Be permissive; everything we can't parse becomes a generic "event".
function extractEvents(body: unknown): TimelineEvent[] {
  if (!body || typeof body !== "object") return [];
  const obj = body as Record<string, unknown>;
  const arr =
    (Array.isArray(obj.events) ? obj.events : null) ||
    (Array.isArray(obj.moments) ? obj.moments : null);
  if (!arr) return [];
  return arr
    .map((entry): TimelineEvent | null => {
      if (!entry || typeof entry !== "object") return null;
      const o = entry as Record<string, unknown>;
      const text =
        typeof o.text === "string"
          ? o.text
          : typeof o.summary === "string"
            ? o.summary
            : typeof o.label === "string"
              ? o.label
              : "";
      if (!text) return null;
      const kindRaw = typeof o.kind === "string" ? o.kind : "event";
      return {
        ts: typeof o.ts === "number" ? o.ts : undefined,
        kind: kindRaw.toLowerCase(),
        text,
      };
    })
    .filter((e): e is TimelineEvent => e !== null);
}

function formatTs(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
